/**
 * The filter row every report page shares — property, owner, and/or tenant,
 * plus Export CSV / Export PDF. One component rather than eight copies of the
 * same three `<select>`s and two `<a>` tags, so every report feels like one
 * product area instead of eight pages that happen to share a CSS file.
 *
 * Only renders the selects a given report actually supports — a vacant unit
 * has no current tenant, and deposits held is grouped by property, so those
 * two omit `onTenantChange`.
 *
 * Export links are plain anchors, not `api()` fetches: the response is a
 * file, the browser already carries the session cookie same-origin, and a
 * fetch-then-blob dance would gain nothing here.
 */

import { useList } from '@refinedev/core';

interface PropertyOption {
  id: string;
  name: string;
}

interface OwnerOption {
  id: string;
  displayName: string;
}

interface TenantOption {
  id: string;
  firstName: string;
  lastName: string;
}

export interface ReportFilterBarProps {
  propertyId?: string;
  onPropertyChange?: (value: string) => void;
  /** The property select's empty option. "All properties" by default, since
   *  property is an optional narrowing filter on most reports; the property
   *  report, where one is required, says "Select a property" instead. */
  propertyEmptyLabel?: string;
  ownerId?: string;
  onOwnerChange?: (value: string) => void;
  tenantId?: string;
  onTenantChange?: (value: string) => void;
  /** The API path this report reads from, e.g. `/reports/ar-aging`. */
  exportPath: string;
  /** Every filter currently in effect, property/owner/tenant included — built into the export query string. */
  exportParams: Record<string, string | undefined>;
  /** Extra report-specific controls (asOf, minDaysLate, withinDays, a date range) rendered inline, before the export buttons. */
  children?: React.ReactNode;
}

function buildQuery(params: Record<string, string | undefined>, format: 'csv' | 'pdf'): string {
  const search = new URLSearchParams();
  search.set('format', format);
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value);
  }
  return search.toString();
}

export function ReportFilterBar({
  propertyId,
  onPropertyChange,
  propertyEmptyLabel = 'All properties',
  ownerId,
  onOwnerChange,
  tenantId,
  onTenantChange,
  exportPath,
  exportParams,
  children,
}: ReportFilterBarProps) {
  const { data: propertiesData } = useList<PropertyOption>({
    resource: 'properties',
    pagination: { current: 1, pageSize: 200 },
    queryOptions: { enabled: onPropertyChange !== undefined },
  });

  const { data: ownersData } = useList<OwnerOption>({
    resource: 'owners',
    pagination: { current: 1, pageSize: 200 },
    queryOptions: { enabled: onOwnerChange !== undefined },
  });

  const { data: tenantsData } = useList<TenantOption>({
    resource: 'tenants',
    pagination: { current: 1, pageSize: 200 },
    queryOptions: { enabled: onTenantChange !== undefined },
  });

  return (
    <div className="row report-filter-bar">
      {onPropertyChange ? (
        <select
          className="field__input"
          value={propertyId ?? ''}
          onChange={(event) => onPropertyChange(event.target.value)}
        >
          <option value="">{propertyEmptyLabel}</option>
          {(propertiesData?.data ?? []).map((property) => (
            <option key={property.id} value={property.id}>
              {property.name}
            </option>
          ))}
        </select>
      ) : null}

      {onOwnerChange ? (
        <select
          className="field__input"
          value={ownerId ?? ''}
          onChange={(event) => onOwnerChange(event.target.value)}
        >
          <option value="">Select an owner</option>
          {(ownersData?.data ?? []).map((owner) => (
            <option key={owner.id} value={owner.id}>
              {owner.displayName}
            </option>
          ))}
        </select>
      ) : null}

      {onTenantChange ? (
        <select
          className="field__input"
          value={tenantId ?? ''}
          onChange={(event) => onTenantChange(event.target.value)}
        >
          <option value="">Select a tenant</option>
          {(tenantsData?.data ?? []).map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.firstName} {tenant.lastName}
            </option>
          ))}
        </select>
      ) : null}

      {children}

      <span className="report-filter-bar__export">
        <a
          className="button button--quiet"
          href={`/api${exportPath}?${buildQuery(exportParams, 'csv')}`}
        >
          Export CSV
        </a>
        <a
          className="button button--quiet"
          href={`/api${exportPath}?${buildQuery(exportParams, 'pdf')}`}
        >
          Export PDF
        </a>
      </span>
    </div>
  );
}
